import { Tooltip, DropdownItem } from '@distribusion/owl-frontend-ui'
import { Field, useFormikContext } from 'formik'
import React, { ReactElement, useCallback, useMemo } from 'react'

import { useTranslation } from '@lib/i18n'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import DropdownField from '@ui/Dropdown/Field'

interface PassengerTypeProps {
  name: string
  types: Passenger.Type[]
  disabled?: boolean
  validate?: () => void
  onChange?: (type: string) => void
}

const Type = ({ name, types, disabled, validate, onChange }: PassengerTypeProps): ReactElement => {
  const { t } = useTranslation()
  const { setFieldValue, values } = useFormikContext<CheckoutFormData>()
  const handleChange = (type: string): void => {
    setFieldValue(`${name}.cards`, [])
    onChange?.(type)
  }

  const errorMessage = validate?.()
  const getAmountByType = useCallback(
    (type: string): number => values.passengers.filter(({ type: passengerType }) => passengerType === type).length,
    [values.passengers],
  )

  const options = useMemo(
    () =>
      types.map<DropdownItem<string>>(({ id, code, minAge, maxAge, maxAmountPerBooking }) => {
        const isMaxAmount = maxAmountPerBooking ? getAmountByType(code) >= maxAmountPerBooking : false
        const label = t('checkout.passengerDetails.passengerTypeLabel', {
          name: t(`searchBar.passengers.codes.${code}`),
          minAge,
          maxAge,
        })

        return {
          value: id,
          disabled: isMaxAmount,
          label: (
            <Tooltip
              hideWhenHovered
              position="top"
              action="hover"
              content={isMaxAmount ? t('passengers.maxAmountPerBooking', { count: maxAmountPerBooking }) : null}
              data-tag={`passenger-type-tooltip-${code}`}
            >
              {label}
            </Tooltip>
          ),
          selectedLabel: label,
        }
      }),
    [types, getAmountByType, t],
  )

  return (
    <Field
      component={DropdownField}
      label={t('checkout.passengerDetails.passengerType')}
      name={`${name}.type`}
      items={options}
      disabled={disabled}
      validate={validate}
      errorMessage={errorMessage}
      onChange={handleChange}
      ariaLabel={t('checkout.passengerDetails.passengerType')}
    />
  )
}

export default Type
